import { Link } from "react-router-dom";
import { useState } from "react";
import { MapPin, Phone, Clock, MessageCircle, Instagram, Facebook } from "lucide-react";
import Logo from "./Logo";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { CONTACTS, NAV_LINKS, SOCIALS, whatsappLink } from "../data/site";

export default function Footer() {
    const [legalOpen, setLegalOpen] = useState(false);
    return (
        <footer data-testid="site-footer" className="bg-ink text-white">
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-16 sm:px-8 md:grid-cols-12 md:px-12 md:py-20">
                <div className="md:col-span-5">
                    <Logo light />
                    <p className="mt-6 max-w-sm text-sm font-light leading-relaxed text-white/60">
                        Dal 1990 a Concorezzo: odontoiatra, odontotecnico e
                        igienista dentale. Il tuo sorriso progettato e curato
                        dalle stesse mani.
                    </p>
                    <a
                        href={whatsappLink()}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-whatsapp-button"
                        className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-wa px-6 py-3 text-sm font-normal text-white transition-colors duration-300 hover:bg-wa-dark"
                    >
                        <MessageCircle className="h-4 w-4" />
                        Scrivici su WhatsApp
                    </a>
                    <div className="mt-8 flex items-center gap-3" data-testid="footer-socials">
                        <a
                            href={SOCIALS.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Instagram dello studio"
                            data-testid="footer-instagram"
                            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white/70 transition-all duration-300 hover:border-petrol hover:bg-petrol hover:text-white"
                        >
                            <Instagram className="h-4 w-4" />
                        </a>
                        <a
                            href={SOCIALS.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Facebook dello studio"
                            data-testid="footer-facebook"
                            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white/70 transition-all duration-300 hover:border-petrol hover:bg-petrol hover:text-white"
                        >
                            <Facebook className="h-4 w-4" />
                        </a>
                    </div>
                </div>

                <nav className="md:col-span-3" aria-label="Link di footer">
                    <p className="font-logo text-xs font-light uppercase tracking-[0.3em] text-white/40">
                        Navigazione
                    </p>
                    <ul className="mt-6 space-y-3">
                        {NAV_LINKS.map((l) => (
                            <li key={l.to}>
                                <Link
                                    to={l.to}
                                    data-testid={`footer-${l.testid}`}
                                    className="text-sm font-light text-white/70 transition-colors hover:text-white"
                                >
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className="md:col-span-4">
                    <p className="font-logo text-xs font-light uppercase tracking-[0.3em] text-white/40">
                        Contatti
                    </p>
                    <ul className="mt-6 space-y-4 text-sm font-light text-white/70">
                        <li className="flex items-start gap-3">
                            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-petrol" />
                            <a
                                href={CONTACTS.mapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="transition-colors hover:text-white"
                                data-testid="footer-address"
                            >
                                {CONTACTS.address}
                            </a>
                        </li>
                        <li className="flex items-start gap-3">
                            <Phone className="mt-0.5 h-4 w-4 shrink-0 text-petrol" />
                            <a
                                href={CONTACTS.phoneHref}
                                className="transition-colors hover:text-white"
                                data-testid="footer-phone"
                            >
                                {CONTACTS.phoneDisplay}
                            </a>
                        </li>
                        <li className="flex items-start gap-3">
                            <Clock className="mt-0.5 h-4 w-4 shrink-0 text-petrol" />
                            <div data-testid="footer-hours">
                                {CONTACTS.hours.map((h) => (
                                    <p key={h.days}>
                                        <span className="text-white/50">{h.days}:</span>{" "}
                                        {h.time}
                                    </p>
                                ))}
                            </div>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="border-t border-white/10">
                <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-6 text-xs font-light text-white/40 sm:flex-row sm:items-center sm:justify-between sm:px-8 md:px-12">
                    <p data-testid="footer-copyright">
                        © 2026 Studio Dentistico Dr. Mario Massironi — Concorezzo (MB)
                    </p>
                    <p data-testid="footer-legal">
                        Direttore Sanitario: Dr. Mario Massironi, Odontoiatra e
                        Odontotecnico
                    </p>
                    <Dialog open={legalOpen} onOpenChange={setLegalOpen}>
                        <DialogTrigger asChild>
                            <button
                                type="button"
                                data-testid="footer-privacy-link"
                                className="text-left text-white/40 underline-offset-4 transition-colors hover:text-white/70 hover:underline"
                            >
                                Privacy &amp; Cookie
                            </button>
                        </DialogTrigger>
                        <DialogContent
                            data-testid="footer-privacy-dialog"
                            className="max-h-[85vh] w-[calc(100%-2rem)] max-w-2xl overflow-y-auto rounded-3xl border-line"
                        >
                            <DialogHeader>
                                <DialogTitle className="font-serif text-2xl font-normal text-ink">
                                    Privacy &amp; Cookie Policy
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-5 text-sm font-light leading-relaxed text-body">
                                <div>
                                    <p className="mb-1.5 font-medium text-ink">Titolare del trattamento</p>
                                    <p>
                                        Studio Dentistico Dr. Mario Massironi — {CONTACTS.address}. Per
                                        qualsiasi richiesta relativa ai tuoi dati puoi contattarci al numero {CONTACTS.phoneDisplay}.
                                    </p>
                                </div>
                                <div>
                                    <p className="mb-1.5 font-medium text-ink">Dati raccolti</p>
                                    <p>
                                        Questo sito è una vetrina informativa e non prevede registrazione
                                        né area riservata. Se utilizzi il modulo di contatto, i dati che
                                        inserisci (nome, telefono, messaggio) vengono usati esclusivamente
                                        per rispondere alla tua richiesta di appuntamento, inviati tramite
                                        WhatsApp dal tuo dispositivo, e non vengono conservati in alcun
                                        database del sito.
                                    </p>
                                </div>
                                <div>
                                    <p className="mb-1.5 font-medium text-ink">Cookie</p>
                                    <p>
                                        Il sito non utilizza cookie di profilazione a scopo pubblicitario.
                                        Alcuni contenuti incorporati da terze parti (mappa di Google Maps,
                                        collegamenti a Instagram e Facebook) possono installare cookie tecnici
                                        o di terze parti nel momento in cui vengono caricati o cliccati:
                                        continuando la navigazione o interagendo con tali contenuti acconsenti
                                        al loro utilizzo. Puoi gestire o disabilitare i cookie dalle
                                        impostazioni del tuo browser.
                                    </p>
                                </div>
                                <div>
                                    <p className="mb-1.5 font-medium text-ink">I tuoi diritti</p>
                                    <p>
                                        Ai sensi del Regolamento UE 2016/679 (GDPR) hai diritto di accedere,
                                        rettificare o chiedere la cancellazione dei tuoi dati. Per esercitare
                                        questi diritti contatta direttamente lo studio.
                                    </p>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>
        </footer>
    );
}
